/* ===================== INSTRUCTOR OVERVIEW =====================
   Role-specific Dashboard screen for instructor accounts, rendered
   through TAB_ROLE_COMPONENTS in tabs/index.js. Instructors can't
   reach the consultation/horoscope tabs the seeker overview links
   to (roles.js TAB_ACCESS), so their overview is a teaching console
   at a glance: course + live-class stats, the My Courses shortlist
   and the next few live sessions. Full management (lessons, topics,
   recordings library, delete) lives in the Academy tab
   (InstructorCoursesTab) — every "view all" here switches to it.

   Everything is live — no demo rows or fallback numbers: courses
   ride useInstructorCourses (GET /courses filtered to this
   instructor) and classes ride useLiveClasses (GET /live-classes,
   server-scoped to the signed-in instructor). Every value shows a
   pulsing skeleton while its query loads and an em dash / retry
   state when the read fails, never a made-up number. The create
   CTAs open CreateCourseModal (POST /courses) and
   CreateLiveClassModal (POST /live-classes) right here. */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FiBookOpen, FiCalendar, FiRadio, FiVideo } from 'react-icons/fi'
import { useInstructorCourses, useLiveClasses } from '../useDashboard'
import { Badge, ClassRow, ClassSkeletonCard, isScheduledRow } from './academyShared'
import CreateLiveClassModal from './CreateLiveClassModal'
import CreateCourseModal from './CreateCourseModal'

/* The overview shows only the first N of each list (live classes
   first) — the full lists live in the Academy tab. */
const CLASS_LIMIT = 5
const COURSE_LIMIT = 3

export default function InstructorOverviewTab({ onSwitch }) {
  const { classes, total, hasClasses, isLoading, isError, refetch } = useLiveClasses()
  const {
    courses,
    hasCourses,
    isLoading: coursesLoading,
    isError: coursesError,
    refetch: refetchCourses,
  } = useInstructorCourses()
  const [showCreate, setShowCreate] = useState(false)
  const [showCreateCourse, setShowCreateCourse] = useState(false)

  // Deep link into the Academy tab with a course open (?tab=courses&course=id)
  // — same URL contract InstructorCoursesTab reads, so back returns here.
  const [searchParams, setSearchParams] = useSearchParams()
  const openCourseInAcademy = (id) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'courses')
    next.set('course', String(id))
    setSearchParams(next)
  }

  const liveNowCount = classes.filter((c) => c.isLive).length
  const scheduledCount = classes.filter(isScheduledRow).length
  // Live classes float to the top; the stable sort keeps API order otherwise.
  const rows = [...classes].sort((a, b) => Number(b.isLive) - Number(a.isLive)).slice(0, CLASS_LIMIT)
  const courseRows = courses.slice(0, COURSE_LIMIT)

  /* Skeleton while the query loads, live number once it lands, em dash if
     the read failed — a failed read must never render as a real "0". */
  const classStat = (n) => (isLoading ? null : isError ? '—' : String(n))
  const courseStat = coursesLoading ? null : coursesError ? '—' : String(courses.length)

  return (
    <div className="space-y-8">
      {/* ── Teaching console strip — the instructor counterpart of the astrologer console ── */}
      <div className="relative overflow-hidden rounded-2xl bg-[#3D0A0A] text-white p-5 shadow-lg shadow-[#3D0A0A]/15">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D48806]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 left-1/3 w-44 h-44 bg-red-900/40 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[8px] uppercase font-bold tracking-[0.3em] text-[#D4A017]">Instructor Dashboard</p>
            <h2 className="text-lg font-royal text-[#FBF3E4] mt-1">Your academy at a glance</h2>
            <p className="text-[10px] text-white/50 mt-0.5">Manage your courses and live classes, and track engagement.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowCreateCourse(true)}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
            >
              + Create Course
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="border border-white/15 hover:border-[#D4A017]/60 text-[#FBF3E4] hover:text-[#D4A017] font-semibold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl transition-all"
            >
              + Live Class
            </button>
            <button onClick={() => onSwitch('earnings')} className="border border-white/15 hover:border-[#D4A017]/60 text-[#FBF3E4] hover:text-[#D4A017] font-semibold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl transition-all">Earnings</button>
          </div>
        </div>
      </div>

      {/* ── Teaching stats — first four of the Academy tab's strip, same order:
            courses ride the course read, the rest ride GET /live-classes ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard tone="#3D0A0A" label="Courses" icon={<FiBookOpen size={20} />}
          value={courseStat}
          caption="Manage in Academy" onClick={() => onSwitch('courses')} />
        <StatCard tone="#D48806" label="Total Classes" icon={<FiVideo size={20} />}
          value={classStat(total)}
          caption="All time" />
        <StatCard tone="#3D0A0A" label="Live Now" icon={<FiRadio size={20} />}
          value={classStat(liveNowCount)}
          caption="On air" />
        <StatCard tone="#D48806" label="Scheduled" icon={<FiCalendar size={20} />}
          value={classStat(scheduledCount)}
          caption="Upcoming sessions" />
      </div>

      {/* ── My Courses — shortlist of the courses this instructor teaches;
            each row opens its details page in the Academy tab ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">My Courses</h3>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateCourse(true)}
            className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all shrink-0"
          >
            + New Course
          </button>
        </div>

        {coursesLoading ? (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <ClassSkeletonCard key={i} />
            ))}
          </div>
        ) : coursesError ? (
          <RetryBlock
            icon="🛰️"
            title="Couldn’t load your courses"
            body="Something went wrong while fetching your courses. Please try again."
            onRetry={refetchCourses}
          />
        ) : !hasCourses ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">📚</div>
            <p className="text-base font-semibold text-astroDark mb-1">No courses yet</p>
            <p className="text-sm text-stone-400 mb-4">Create your first course, then add lessons and schedule live classes.</p>
            <button
              type="button"
              onClick={() => setShowCreateCourse(true)}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
            >
              Create your first course
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {courseRows.map((course) => (
              <button
                key={course.id}
                type="button"
                onClick={() => openCourseInAcademy(course.id)}
                className="group text-left w-full flex gap-3 items-center border border-stone-100 rounded-xl p-3 hover:shadow-md hover:border-[#D4A017]/40 transition-all"
              >
                {course.img ? (
                  <img src={course.img} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-[#F5E6D3] text-[#A67C00] text-xl flex items-center justify-center shrink-0">📚</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-stone-900 leading-tight truncate group-hover:text-[#A67C00] transition-colors">{course.title}</p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                    {course.category && <Badge>{course.category}</Badge>}
                    {course.level && <Badge className="bg-stone-100 text-stone-500 capitalize">{course.level}</Badge>}
                    <Badge className={course.isLiveCourse ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-600'}>
                      {course.isLiveCourse ? '🔴 Live Course' : '🎬 Recorded'}
                    </Badge>
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-goldAccent shrink-0">Manage →</span>
              </button>
            ))}
            {courses.length > COURSE_LIMIT && (
              <button
                type="button"
                onClick={() => onSwitch('courses')}
                className="block w-full text-center text-xs font-bold text-[#8B4513] uppercase tracking-wider hover:underline pt-1"
              >
                View all {courses.length} courses
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── My Live Classes — the next few sessions, shared ClassRow chrome ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">My Live Classes</h3>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all shrink-0"
          >
            + New Class
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <ClassSkeletonCard key={i} />
            ))}
          </div>
        ) : isError ? (
          <RetryBlock
            icon="🛰️"
            title="Couldn’t load your classes"
            body="Something went wrong while fetching your live classes. Please try again."
            onRetry={refetch}
          />
        ) : !hasClasses ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">📹</div>
            <p className="text-base font-semibold text-astroDark mb-1">No live classes yet</p>
            <p className="text-sm text-stone-400 mb-4">Schedule your first session and it will appear here.</p>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
            >
              Create your first class
            </button>
          </div>
        ) : (
          <>
            <div className="divide-y divide-stone-50 -mx-2">
              {rows.map((c) => <ClassRow key={c.key} c={c} />)}
            </div>
            {classes.length > CLASS_LIMIT && (
              <button
                type="button"
                onClick={() => onSwitch('courses')}
                className="block w-full text-center text-xs font-bold text-[#8B4513] uppercase tracking-wider hover:underline pt-3 mt-1 border-t border-stone-50"
              >
                View all {total} classes
              </button>
            )}
          </>
        )}
      </div>

      {showCreate && <CreateLiveClassModal onClose={() => setShowCreate(false)} />}
      {showCreateCourse && <CreateCourseModal onClose={() => setShowCreateCourse(false)} />}
    </div>
  )
}

/* Failed-read state shared by both cards — message + retry, never fake rows. */
function RetryBlock({ icon, title, body, onRetry }) {
  return (
    <div className="py-10 text-center">
      <div className="text-4xl mb-3 opacity-40">{icon}</div>
      <p className="text-base font-semibold text-astroDark mb-1">{title}</p>
      <p className="text-sm text-stone-400 mb-4">{body}</p>
      <button
        type="button"
        onClick={() => onRetry()}
        className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
        style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
      >
        Retry
      </button>
    </div>
  )
}

/* Stat card on the design system's .stat-card surface (rounded, hover lift,
   top accent bar driven by --accent) — same surface and vertical layout as
   the other role overviews so all dashboards read as one family: icon chip
   + label row, the live value (pulse placeholder while its query loads),
   then the caption. */
function StatCard({ tone, label, value, caption, onClick, icon }) {
  return (
    <div className="stat-card h-full flex flex-col" style={{ '--accent': tone }}>
      <div className="flex items-center gap-3">
        <div className="stat-icon shrink-0 text-white" style={{ background: tone }}>
          {icon}
        </div>
        <p className="text-stone-500 text-[11px] font-bold uppercase tracking-wider leading-snug">{label}</p>
      </div>
      {value == null ? (
        <div className="h-6 w-16 bg-stone-100 rounded-full mt-3.5 mb-1 animate-pulse" />
      ) : (
        <h3 className="text-[22px] font-bold text-stone-900 mt-3 leading-tight break-words">{value}</h3>
      )}
      {onClick ? (
        <button onClick={onClick} className="mt-1 w-max text-left text-xs font-semibold text-[#A67C00] hover:underline cursor-pointer">{caption}</button>
      ) : (
        <span className="mt-1 text-xs font-semibold text-[#A67C00]">{caption}</span>
      )}
    </div>
  )
}
